
import { Link } from 'react-router-dom';
import { Eye, Flag, Info, MailCheck, MoreVertical, PencilLine, Phone, RotateCcw, Upload, UserX, Users } from 'lucide-react';

import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { Menu } from '@/components/ui/Menu';

import { Table, type Column } from '@/components/ui/Table';
import { gradeFor, gradeToneClasses } from '@/constants/grading';
import { NOT_ASSIGNED_LABEL } from '@/constants/houses';
import { classDisplayName } from '@/features/institute/classes/utils';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { instituteToday } from '@/features/teacher/utils';
import type { FeeStanding, StudentListItem } from '@/types/student';

import { ageOn, formatAgeShort } from '@/features/institute/admissions/utils';
import {
  GUARDIAN_LABEL,
  STUDENT_LOGIN_LABEL,
  STUDENT_LOGIN_TONE,
  STUDENT_STATUS_LABEL,
  STUDENT_STATUS_TONE,
  TODAY_ATTENDANCE_LABEL,
  TODAY_ATTENDANCE_TONE,
  TODAY_NOT_MARKED_LABEL,
} from '../constants';
import { StudentAvatar } from './StudentAvatar';

/** How the fee column names each standing, and what colour it wears. */
const FEE_LABEL: Record<FeeStanding, string> = {
  PAID: 'Paid',
  PARTIAL: 'Partial',
  UNPAID: 'Unpaid',
};

const FEE_TONE: Record<FeeStanding, BadgeTone> = {
  PAID: 'success',
  PARTIAL: 'warning',
  UNPAID: 'critical',
};

/** An em dash, for a cell the server has nothing to put in. */
function Blank() {
  return <span className="text-ink-3">—</span>;
}

/**
 * Who the office rings about this child, under their name — "Father · Ramesh
 * Kumar". Folded into the student cell rather than given a column of its own:
 * it is read *with* the name, and the separate column cost the fees and the
 * attendance their room on a laptop screen.
 *
 * A college student often has no guardian on file; the line then falls back to
 * their own email or mobile, as the old Contact column did. Nothing at all is
 * drawn when there is neither.
 */
function ContactLine({ row }: { row: StudentListItem }) {
  const email = row.studentEmail || row.parentEmail || row.email;
  const name = row.primaryContactName ?? email;
  if (!name) return null;

  const label = (row.primaryContactName && row.primaryContactKind) ? GUARDIAN_LABEL[row.primaryContactKind] : 'Email';
  const phone = row.primaryContactMobile;

  return (
    <>
      <span className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
        <span className="font-semibold">{label}</span> · {name}
      </span>
      {phone && (
        // A tel: link, so on a phone the office can ring the family in one tap.
        <a
          href={`tel:${phone}`}
          className="inline-flex w-fit items-center gap-1 truncate rounded font-mono text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
        >
          <Phone className="h-3 w-3 shrink-0" aria-label="Phone" />
          {phone}
        </a>
      )}
    </>
  );
}

/**
 * "Class - 9 · Section A · 34", under the name — the class as the classes
 * roster names it, the section, and that section's whole strength (not just
 * the rows a filter left on screen). Nothing for a child placed nowhere.
 */
function PlacementLine({ row }: { row: StudentListItem }) {
  if (row.sectionStudentCount === null) return null;
  const strength = `${row.sectionStudentCount} ${row.sectionStudentCount === 1 ? 'student' : 'students'} in this section`;

  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-xs font-semibold text-ink-2">
      <span className="truncate">
        {classDisplayName(row.className)} · Section {row.sectionName}
      </span>
      <span
        className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-surface-2 px-1 text-[11px] font-bold tabular-nums"
        aria-label={strength}
        title={strength}
      >
        <Users className="h-3 w-3" aria-hidden />
        {row.sectionStudentCount}
      </span>
    </span>
  );
}

/**
 * "DOB · 12 Mar 2012 (14y)", under the name. Read with the student rather than
 * in a column of its own, for the same reason as the contact line below it.
 * Left out when no date is on file.
 */
function BirthLine({ dateOfBirth }: { dateOfBirth: string | null }) {
  if (!dateOfBirth) return null;
  const age = ageOn(dateOfBirth.slice(0, 10), new Date());

  return (
    <span className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
      <span className="font-semibold">DOB</span> · {formatDate(dateOfBirth)}
      {age && ` (${formatAgeShort(age)})`}
    </span>
  );
}

/** A column heading with a note about what the figures under it mean. */
function HeadingWithNote({ label, note }: { label: string; note: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <Info className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-label={note} />
    </span>
  );
}

export interface StudentsRosterTableProps {
  rows: StudentListItem[];
  isLoading: boolean;
  empty: React.ReactNode;
  /** False at a college, which places nobody in a class or section. */
  hasPlacement: boolean;
  canManage: boolean;
  /** The uuids ticked on this page. */
  selected: Set<string>;
  onToggleRow: (uuid: string) => void;
  onToggleAll: () => void;
  onOpen: (student: StudentListItem) => void;
  onEdit: (student: StudentListItem) => void;
  onResendInvite: (student: StudentListItem) => void;
  onResendGuardianInvite: (student: StudentListItem) => void;
  onSuspend: (student: StudentListItem) => void;
  onReinstate: (student: StudentListItem) => void;
  onChangeHouse: (student: StudentListItem) => void;
  attendanceMonth?: string;
}

/**
 * The register itself.
 *
 * One column per question: who the child is (with their class, section and
 * family folded under the name), who runs their section, what they owe, how
 * they are doing, whether they come in, and where they stand. Everything the columns
 * show comes from `GET /institute/students` — nothing here is computed from a
 * guess, and a child the server has no fee record, register or marks for gets a
 * dash rather than a zero.
 */
interface StudentAttendanceBarProps {
  present: number;
  leave: number;
  absent: number;
  /** The server's figure; null where no day counted either way. */
  percentage: number | null;
  fullName: string;
  todayBadge: React.ReactNode;
}

function StudentAttendanceBar({
  present,
  leave,
  absent,
  percentage,
  fullName,
  todayBadge,
}: StudentAttendanceBarProps) {
  const total = present + leave + absent;
  // The rate is the server's, never worked out again here: leave counts
  // neither way there, and a second sum that put it under the line would show
  // a different figure from the profile for the same child.
  const rate = percentage === null ? '—' : `${percentage}%`;

  const presentWidth = total > 0 ? (present / total) * 100 : 0;
  const leaveWidth = total > 0 ? (leave / total) * 100 : 0;
  const absentWidth = total > 0 ? (absent / total) * 100 : 0;

  return (
    <div
      className="flex flex-col gap-1.5 min-w-[175px] max-w-[210px]"
      aria-label={`${fullName}'s attendance: ${rate}, ${present} present, ${leave} leave, ${absent} absent`}
    >
      {/* Top row: Today badge on the left, Overall Percentage on the right */}
      <div className="flex items-center justify-between gap-2">
        {todayBadge}
        <span className="text-sm font-bold tabular-nums text-slate-800 dark:text-slate-100">
          {rate}
        </span>
      </div>

      {/* Modern multi-segment progress bar */}
      <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 flex overflow-hidden">
        {present > 0 && (
          <div
            style={{ width: `${presentWidth}%` }}
            className="bg-teal-500 h-full transition-all duration-300"
            title={`Present: ${present} (${Math.round(presentWidth)}%)`}
          />
        )}
        {leave > 0 && (
          <div
            style={{ width: `${leaveWidth}%` }}
            className="bg-amber-400 h-full transition-all duration-300"
            title={`Leave: ${leave} (${Math.round(leaveWidth)}%)`}
          />
        )}
        {absent > 0 && (
          <div
            style={{ width: `${absentWidth}%` }}
            className="bg-rose-500 h-full transition-all duration-300"
            title={`Absent: ${absent} (${Math.round(absentWidth)}%)`}
          />
        )}
      </div>

      {/* Counts with distinct colors for each status */}
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full bg-teal-500 shrink-0" aria-hidden />
          <span>Present:</span>
          <span className="font-bold tabular-nums text-slate-900 dark:text-slate-100 text-xs">{present}</span>
        </span>

        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" aria-hidden />
          <span>Leave:</span>
          <span className="font-bold tabular-nums text-slate-900 dark:text-slate-100 text-xs">{leave}</span>
        </span>

        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" aria-hidden />
          <span>Absent:</span>
          <span className="font-bold tabular-nums text-slate-900 dark:text-slate-100 text-xs">{absent}</span>
        </span>
      </div>
    </div>
  );
}

export function StudentsRosterTable({
  rows,
  isLoading,
  empty,
  hasPlacement,
  canManage,
  selected,
  onToggleRow,
  onToggleAll,
  onOpen,
  onEdit,
  onResendInvite,
  onResendGuardianInvite,
  onSuspend,
  onReinstate,
  onChangeHouse,
  attendanceMonth,
}: StudentsRosterTableProps) {
  const allTicked = rows.length > 0 && rows.every((row) => selected.has(row.uuid));
  const someTicked = rows.some((row) => selected.has(row.uuid));

  const money = (amount: string, className?: string) => (
    <span className={cn('text-sm font-[560] tabular-nums', className)}>{formatCurrency(amount)}</span>
  );

  const columns: Column<StudentListItem>[] = [
    {
      key: 'select',
      align: 'center',
      sticky: 'left',
      stickyWidth: 38,
      className: '!px-2 !w-[38px] !min-w-[38px] !max-w-[38px]',
      headerClassName: '!px-2 !w-[38px] !min-w-[38px] !max-w-[38px]',
      header: (
        <div className="flex items-center justify-center">
          <Checkbox
            label="Select every student on this page"
            hideLabel
            checked={allTicked}
            indeterminate={!allTicked && someTicked}
            onChange={onToggleAll}
            disabled={rows.length === 0}
          />
        </div>
      ),
      render: (row) => (
        <div className="flex items-center justify-center">
          <Checkbox
            label={`Select ${row.fullName}`}
            hideLabel
            checked={selected.has(row.uuid)}
            onChange={() => onToggleRow(row.uuid)}
          />
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      align: 'left',
      sticky: 'left',
      stickyWidth: 260,
      className: '!pl-2 !pr-4',
      headerClassName: '!pl-2 !pr-4',
      render: (row) => (
        <div className="flex items-center gap-3">
          <StudentAvatar
            fullName={row.fullName}
            status={row.status}
            uuid={row.uuid}
            hasPhoto={row.hasPhoto}
          />
          <div className="flex min-w-0 flex-col">
            <button
              type="button"
              onClick={() => onOpen(row)}
              className="truncate text-left text-sm font-extrabold text-slate-900 dark:text-white transition-colors hover:text-accent hover:underline focus:outline-none"
            >
              {row.fullName}
            </button>
            <span className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
              <span className="font-mono">{row.admissionNumber}</span> · Admitted {row.admissionDate.slice(0, 4)}
            </span>
            <BirthLine dateOfBirth={row.dateOfBirth} />
            {hasPlacement && <PlacementLine row={row} />}
            <ContactLine row={row} />
            {/* The link lands on the record's documents section, where the
                papers are attached — not the top of a long form. */}
            {row.missingDocuments.length > 0 && (
              <span className="mt-1 flex flex-wrap items-center gap-2">
                <Badge tone="warning" withDot>
                  {row.missingDocuments.length === 1
                    ? '1 document pending'
                    : `${row.missingDocuments.length} documents pending`}
                </Badge>
                <Link
                  to={`/admin/students/${row.uuid}#documents`}
                  aria-label={`${canManage ? 'Upload' : 'View'} documents for ${row.fullName}`}
                  className="inline-flex items-center gap-1 rounded text-xs font-bold text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                >
                  <Upload className="h-3 w-3" aria-hidden />
                  {canManage ? 'Upload' : 'View'}
                </Link>
              </span>
            )}
          </div>
        </div>
      ),
    },
    // Only once a section has been numbered: before "Assign roll numbers" has
    // run, a column of blanks is a column that says nothing.
    ...(rows.some((row) => row.rollNumber)
      ? [
          {
            key: 'roll',
            header: 'Roll no.',
            align: 'center' as const,
            render: (row: StudentListItem) =>
              row.rollNumber ? (
                <span className="text-base font-bold font-mono text-slate-800 dark:text-slate-200 tabular-nums">{row.rollNumber}</span>
              ) : (
                <Blank />
              ),
          },
        ]
      : []),
    ...(hasPlacement
      ? [
          {
            key: 'classTeacher',
            header: 'Class teacher',
            render: (row: StudentListItem) =>
              row.classTeacherName ? (
                <span className="whitespace-nowrap text-sm font-semibold text-ink">{row.classTeacherName}</span>
              ) : (
                <span className="whitespace-nowrap text-xs font-medium text-ink-3">Not assigned</span>
              ),
          },
        ]
      : []),
    {
      key: 'house',
      header: 'House',
      render: (row: StudentListItem) =>
        row.house ? (
          <span className="whitespace-nowrap text-sm font-semibold text-ink">{row.house.name}</span>
        ) : (
          <span className="whitespace-nowrap text-xs font-medium text-ink-3">{NOT_ASSIGNED_LABEL}</span>
        ),
    },
    {
      key: 'fees',
      header: 'Fees',
      render: (row) => {
        if (!row.fee) return <Blank />;
        const { total, paid, due, status } = row.fee;

        // One column, read top to bottom: what the year costs and its
        // standing, then what has come in and what is still owed.
        return (
          <div className="flex flex-col gap-0.5 whitespace-nowrap text-xs text-slate-600 dark:text-slate-400">
            <span className="flex items-center gap-2">
              {money(total, 'font-bold text-slate-900 dark:text-white')}
              <Badge tone={FEE_TONE[status]}>{FEE_LABEL[status]}</Badge>
            </span>
            <span>
              Paid: {money(paid, 'text-xs font-bold text-emerald-600 dark:text-emerald-400')}
            </span>
            <span>
              Due:{' '}
              {money(
                due,
                due === '0' ? 'text-xs font-medium text-slate-400' : 'text-xs font-bold text-rose-600 dark:text-rose-400',
              )}
            </span>
          </div>
        );
      },
    },
    {
      key: 'performance',
      header: (
        <HeadingWithNote
          label="Academic performance"
          note="Marks obtained over marks available across every paper sat this session. The rank is among classmates who have also been marked."
        />
      ),
      render: (row) => {
        if (!row.performance) return <Blank />;
        const { averagePercent, rank, classSize } = row.performance;

        return (
          <div className="flex flex-col whitespace-nowrap text-xs text-slate-600 dark:text-slate-400">
            {/* Toned by the school's grading scale, as the last-exam column
                is, rather than by thresholds of this table's own. */}
            <span className={cn('text-sm font-bold', gradeToneClasses(gradeFor(averagePercent, 100)).text)}>
              Average: {averagePercent}%
            </span>
            <span>
              Rank: {rank} / {classSize}
            </span>
          </div>
        );
      },
    },
    {
      key: 'lastExam',
      header: (
        <HeadingWithNote
          label="Last exam"
          note="Their most recent exam, every subject paper of it added up: marks obtained over marks available. The grade is worked out from that percentage on the same scale as the marks report. An absent paper counts neither way."
        />
      ),
      render: (row) => {
        if (!row.lastExam) return <Blank />;
        const { name, date, obtained, maxMarks, percent, papersSat, papersAbsent } = row.lastExam;
        const band = papersSat > 0 ? gradeFor(obtained, maxMarks) : null;

        return (
          <div className="flex max-w-[12rem] flex-col gap-0.5 whitespace-nowrap text-xs text-ink-2">
            <span className="truncate text-sm font-semibold text-ink" title={name}>
              {name}
            </span>
            {percent === null ? (
              <span>
                <Badge tone="critical">Absent</Badge>
              </span>
            ) : (
              <span className="flex items-center gap-2">
                {/* Uncoloured: the grade badge carries the tone, from the
                    grading scale, so the two cannot disagree. */}
                <span className="font-bold text-ink">
                  {obtained} / {maxMarks} · {percent}%
                </span>
                {band && (
                  <Badge tone={band.tone}>
                    <span className="sr-only">Grade </span>
                    {band.grade}
                  </Badge>
                )}
              </span>
            )}
            <span className="text-ink-3">
              {formatDate(date)}
              {papersAbsent > 0 && ` · absent from ${papersAbsent} of ${papersSat + papersAbsent}`}
            </span>
          </div>
        );
      },
    },
    {
      key: 'attendance',
      header: (
        <HeadingWithNote
          label="Attendance"
          note="Today's mark (or approved leave), then days present against days marked in the month chosen above the table. Approved leave counts neither way; a late mark counts as present."
        />
      ),
      render: (row) => {
        const isCurrentMonth = !attendanceMonth || attendanceMonth === instituteToday().slice(0, 7);
        const hasAttendanceData = Boolean(
          row.attendance && (row.attendance.present + row.attendance.leave + row.attendance.absent > 0),
        );

        if (!isCurrentMonth && !hasAttendanceData) {
          return (
            <span className="inline-flex items-center rounded-lg border border-slate-200/70 bg-slate-50/70 dark:border-white/10 dark:bg-white/5 px-2.5 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500">
              No attendance data
            </span>
          );
        }

        // Today's mark first: the question the office asks of this column
        // most mornings. "Not marked" rather than a blank, so a register not
        // yet taken reads as that and never as a missing column.
        const today = row.todayAttendance ? (
          <Badge tone={TODAY_ATTENDANCE_TONE[row.todayAttendance]}>
            {TODAY_ATTENDANCE_LABEL[row.todayAttendance]}
          </Badge>
        ) : (
          <Badge tone="neutral" className="bg-slate-200/70 dark:bg-slate-500/70 text-slate-700 dark:text-slate-200 border border-slate-300/60 dark:border-slate-600/60">
            {TODAY_NOT_MARKED_LABEL}
          </Badge>
        );
        if (!row.attendance) {
          return (
            <div className="flex flex-col items-start gap-1.5">
              {today}
            </div>
          );
        }
        const { present, absent, leave, percentage } = row.attendance;

        return (
          <div className="flex flex-col items-start gap-1.5">
          <StudentAttendanceBar
            present={present}
            leave={leave}
            absent={absent}
            // Only leave marked: the server's 0 has nothing under it.
            percentage={present + absent === 0 ? null : percentage}
            fullName={row.fullName}
            todayBadge={today}
          />
        </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (row) => (
        <div className="flex flex-col items-center gap-1">
          <Badge tone={STUDENT_STATUS_TONE[row.status]} className="tracking-wide">
            {STUDENT_STATUS_LABEL[row.status]}
          </Badge>
          {row.login !== 'ACTIVE' && (
            <Badge tone={STUDENT_LOGIN_TONE[row.login]} className="tracking-wide">
              {STUDENT_LOGIN_LABEL[row.login]}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      sticky: 'right',
      stickyWidth: 105,
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          {/* One button, not two: viewing and editing open the same record
              page, so whoever may change it is offered Edit and everybody else
              View. */}
          {!canManage && (
            <button
              type="button"
              aria-label={`Open ${row.fullName}'s record`}
              onClick={() => onOpen(row)}
              className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
              title="View record"
            >
              <Eye className="h-4 w-4" aria-hidden />
            </button>
          )}

          {canManage && (
            <button
              type="button"
              aria-label={`Edit ${row.fullName}'s record`}
              onClick={() => onEdit(row)}
              className="p-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
              title="Edit record"
            >
              <PencilLine className="h-4 w-4" aria-hidden />
            </button>
          )}

          <Menu
            label={`More actions for ${row.fullName}`}
            triggerClassName="p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer rounded-lg border-0 bg-transparent shadow-none"
            trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
            items={[
              {
                key: 'invite',
                label:
                  row.login === 'ACTIVE'
                    ? 'Send a fresh student sign-in link'
                    : 'Send student sign-in link',
                description:
                  row.login === 'NONE' ? "Needs the student's own email on the record" : undefined,
                icon: <MailCheck className="h-4 w-4" />,
                disabled: !canManage,
                onSelect: () => onResendInvite(row),
              },
              {
                key: 'house',
                label: row.house ? 'Change house' : 'Assign house',
                description: row.house ? `Now in ${row.house.name}` : NOT_ASSIGNED_LABEL,
                icon: <Flag className="h-4 w-4" />,
                disabled: !canManage,
                onSelect: () => onChangeHouse(row),
              },
              {
                key: 'guardian-invite',
                label: 'Send parent sign-in link',
                description: 'Opens the parent panel for their father or mother',
                icon: <Users className="h-4 w-4" />,
                disabled: !canManage,
                onSelect: () => onResendGuardianInvite(row),
              },
              row.status === 'SUSPENDED'
                ? {
                    key: 'reinstate',
                    label: 'Reactivate student',
                    description: 'Puts them back on the register as enrolled',
                    icon: <RotateCcw className="h-4 w-4" />,
                    disabled: !canManage,
                    onSelect: () => onReinstate(row),
                  }
                : {
                    key: 'suspend',
                    label: 'Suspend student',
                    // Only somebody on the roll can be suspended — the server
                    // refuses anyone else, so the menu says why up front.
                    description:
                      row.status === 'ACTIVE' || row.status === 'INACTIVE'
                        ? undefined
                        : 'Only a student on the roll can be suspended',
                    icon: <UserX className="h-4 w-4" />,
                    tone: 'danger',
                    disabled: !canManage || (row.status !== 'ACTIVE' && row.status !== 'INACTIVE'),
                    onSelect: () => onSuspend(row),
                  },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <Table
      variant="plain"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.uuid}
      rowClassName={(row) =>
        row.status === 'SUSPENDED'
          ? '!bg-rose-100 hover:!bg-rose-200/80 dark:!bg-rose-950/60 dark:hover:!bg-rose-950/80'
          : undefined
      }
      rowPinClassName={(row) =>
        row.status === 'SUSPENDED'
          ? 'sm:!bg-[#ffe4e6] dark:sm:!bg-[#2e1318] sm:group-hover:!bg-[#fecdd3] dark:sm:group-hover:!bg-[#3b191f]'
          : undefined
      }
      isLoading={isLoading}
      empty={empty}
    />
  );
}
